import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackCTAClick, trackFileUpload, trackFunnelStep, FUNNEL_STEPS } from '../lib/analytics';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import {
  Upload,
  Zap,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Box,
  Brain,
  Loader2,
  Lock,
  Scale,
  RotateCcw,
  Download,
  ShieldCheck,
  Sparkles,
  Clock,
  Mail,
  X,
} from 'lucide-react';
import GoogleAuthButton from '../components/GoogleAuthButton';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple browser fingerprint
const getFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fp', 2, 2);
  const nav = navigator.userAgent + navigator.language + screen.width + screen.height + new Date().getTimezoneOffset();
  return btoa(nav + canvas.toDataURL()).slice(0, 32);
};

// 3D Preview Viewer
const PreviewViewer = ({ previewId }) => {
  const mountRef = useRef(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [supportsLoaded, setSupportsLoaded] = useState(false);
  const [supportsAvailable, setSupportsAvailable] = useState(true);

  useEffect(() => {
    if (!mountRef.current || !previewId) return;

    setModelLoaded(false);
    setSupportsLoaded(false);
    setSupportsAvailable(true);

    const container = mountRef.current;
    const w = container.clientWidth;
    const h = 400;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0f111a);

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 2000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(5, 10, 7);
    scene.add(dirLight);
    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-5, -5, -5);
    scene.add(dirLight2);

    // Grid (Y=0 plane in Three.js)
    const grid = new THREE.GridHelper(200, 20, 0x2a2d3a, 0x1a1d28);
    scene.add(grid);

    const loader = new STLLoader();
    let animId;
    const allMeshes = new THREE.Group();
    // STL files use Z-up, Three.js uses Y-up — rotate the group
    allMeshes.rotation.x = -Math.PI / 2;
    scene.add(allMeshes);

    const fitCamera = () => {
      const box = new THREE.Box3().setFromObject(allMeshes);
      if (box.isEmpty()) return;
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      camera.position.set(center.x + maxDim * 1.2, center.y + maxDim * 0.8, center.z + maxDim * 1.2);
      controls.target.copy(center);
      controls.update();
    };

    // Load model
    fetch(`${API}/api/preview/${previewId}/view/model`)
      .then(r => r.arrayBuffer())
      .then(buf => {
        const geo = loader.parse(buf);
        geo.computeVertexNormals();
        const mat = new THREE.MeshPhongMaterial({ color: 0x6366f1, shininess: 40, transparent: true, opacity: 0.85 });
        const mesh = new THREE.Mesh(geo, mat);
        allMeshes.add(mesh);
        fitCamera();
        setModelLoaded(true);
      })
      .catch(() => setModelLoaded(true));

    // Load supports independently
    fetch(`${API}/api/preview/${previewId}/view/supports`)
      .then(r => { if (r.ok) return r.arrayBuffer(); throw new Error('none'); })
      .then(sBuf => {
        const sGeo = loader.parse(sBuf);
        sGeo.computeVertexNormals();
        const sMat = new THREE.MeshPhongMaterial({ color: 0xf59e0b, shininess: 30, transparent: true, opacity: 0.8 });
        const sMesh = new THREE.Mesh(sGeo, sMat);
        allMeshes.add(sMesh);
        fitCamera();
        setSupportsLoaded(true);
      })
      .catch(() => { setSupportsLoaded(true); setSupportsAvailable(false); });

    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Watermark
    const watermark = document.createElement('div');
    watermark.style.cssText = 'position:absolute;bottom:12px;right:16px;color:rgba(99,102,241,0.35);font-size:11px;font-weight:600;pointer-events:none;';
    watermark.textContent = 'PREVIEW — PrintMind AI';
    container.style.position = 'relative';
    container.appendChild(watermark);

    return () => {
      cancelAnimationFrame(animId);
      renderer.dispose();
    };
  }, [previewId]);

  return (
    <div>
      <div ref={mountRef} className="w-full rounded-xl overflow-hidden border border-text-muted/15" style={{ height: 400 }} data-testid="preview-3d-viewer" />
      {/* Loading progress columns */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${modelLoaded ? 'bg-primary/10 text-primary' : 'bg-bg-light/50 text-text-muted'}`}>
          {modelLoaded ? <CheckCircle className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span className="w-2 h-2 rounded-full" style={{ background: '#6366f1' }} />
          Model {modelLoaded ? 'loaded' : 'loading...'}
        </div>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${supportsLoaded ? (supportsAvailable ? 'bg-amber-500/10 text-amber-400' : 'bg-bg-light/50 text-text-muted') : 'bg-bg-light/50 text-text-muted'}`}>
          {supportsLoaded ? <CheckCircle className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span className="w-2 h-2 rounded-full" style={{ background: '#f59e0b' }} />
          Supports {supportsLoaded ? (supportsAvailable ? 'loaded' : 'N/A') : 'loading...'}
        </div>
      </div>
    </div>
  );
};

const PreviewPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  // "Analyzing → Placing → Finalizing" ticker so users see continuous
  // progress during the 10-20s wait. Pure UX, no real wall-clock link.
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  // StrictMode double-mount guard for the preview_view funnel step.
  const _previewViewFiredRef = useRef(false);

  // Check limit on mount
  useEffect(() => {
    // Mark preview-view as a single funnel step on mount.
    if (!_previewViewFiredRef.current) {
      _previewViewFiredRef.current = true;
      trackFunnelStep(FUNNEL_STEPS.PREVIEW_VIEW);
    }

    const stored = localStorage.getItem('pm_preview_used');
    if (stored) {
      setLimitReached(true);
      return;
    }
    // Also check server-side
    const fp = getFingerprint();
    fetch(`${API}/api/preview/check-limit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Fingerprint': fp },
      body: JSON.stringify({ fingerprint: fp }),
    })
      .then(r => r.json())
      .then(d => { if (d.used) { setLimitReached(true); localStorage.setItem('pm_preview_used', '1'); } })
      .catch(() => {});
  }, []);

  // Drive the loading-step ticker while a generation is running.
  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      setLoadingElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setLoadingElapsed(elapsed);
      // Step thresholds tuned to typical 10-20s server runs.
      if (elapsed < 4) setLoadingStep(0);
      else if (elapsed < 10) setLoadingStep(1);
      else if (elapsed < 18) setLoadingStep(2);
      else setLoadingStep(3);
    }, 500);
    return () => clearInterval(tick);
  }, [loading]);

  const handleFile = useCallback((f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.stl')) { setError('Only STL files are supported.'); return; }
    if (f.size > 25 * 1024 * 1024) { setError('File must be under 25MB.'); return; }
    setError('');
    setFile(f);
    setResult(null);
    setPreviewId(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer?.files?.[0]);
  }, [handleFile]);

  const handleGenerate = async () => {
    if (!file || limitReached) return;
    setLoading(true);
    setError('');
    trackFileUpload('stl', file.size);
    trackFunnelStep(FUNNEL_STEPS.GENERATION_START, { file_size_bytes: file.size });
    const startedAt = Date.now();

    const fp = getFingerprint();
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/api/preview/generate`, {
        method: 'POST',
        headers: { 'X-Fingerprint': fp },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        if (res.status === 429) {
          setLimitReached(true);
          localStorage.setItem('pm_preview_used', '1');
        }
        throw new Error(err.detail || 'Generation failed');
      }
      const data = await res.json();
      setPreviewId(data.preview_id);

      // Poll for completion
      const poll = async () => {
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 2000));
          const sRes = await fetch(`${API}/api/preview/${data.preview_id}/status`);
          const sData = await sRes.json();
          if (sData.status === 'completed') {
            setResult(sData);
            localStorage.setItem('pm_preview_used', '1');
            document.cookie = 'pm_preview=1;max-age=259200;path=/';
            const durationMs = Date.now() - startedAt;
            trackFunnelStep(FUNNEL_STEPS.GENERATION_COMPLETE, {
              duration_ms: durationMs,
              saved_percent: sData?.support_estimate?.saved_percent || null,
            });
            return;
          }
          if (sData.status === 'failed') throw new Error('Generation failed. Try a different model.');
        }
        throw new Error('Generation timed out.');
      };
      await poll();
    } catch (e) {
      const reason = e.message || 'Failed to generate preview.';
      setError(reason);
      trackFunnelStep(FUNNEL_STEPS.GENERATION_FAILED, { reason: reason.slice(0, 120) });
    } finally {
      setLoading(false);
    }
  };

  // Limit reached state
  if (limitReached && !result) {
    return (
      <div className="min-h-screen bg-bg-dark">
        <nav className="border-b border-text-muted/10 bg-bg-dark/95 backdrop-blur-md sticky top-0 z-40">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <div className="relative">
                <Brain className="w-7 h-7 text-primary" />
                <Box className="w-3.5 h-3.5 text-accent absolute -bottom-0.5 -right-0.5" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">PrintMind AI</span>
            </button>
            <button onClick={() => navigate('/register')} className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition">
              Sign Up Free
            </button>
          </div>
        </nav>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="bg-bg-light rounded-2xl border border-primary/20 p-8">
            <Lock className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-text-primary mb-3">You've used your free preview</h2>
            <p className="text-text-secondary text-sm mb-6">Create a free account to generate unlimited previews, download optimized STL files, and start saving filament.</p>
            <button
              onClick={() => { trackCTAClick('Create Free Account', 'limit-gate'); navigate('/register'); }}
              className="px-8 py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 mx-auto shadow-lg shadow-primary/25 hover:scale-[1.02] transition-all"
              data-testid="limit-signup-btn"
            >
              <Zap className="w-5 h-5" />
              Create Free Account
            </button>
            <p className="text-xs text-text-muted mt-3">Takes 10 seconds — no credit card required</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-dark">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://printmindai.com/' },
          { name: 'Free Demo', url: 'https://printmindai.com/preview' },
        ]}
      />
      {/* Navbar */}
      <nav className="border-b border-text-muted/10 bg-bg-dark/95 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2">
            <div className="relative">
              <Brain className="w-7 h-7 text-primary" />
              <Box className="w-3.5 h-3.5 text-accent absolute -bottom-0.5 -right-0.5" />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">PrintMind AI</span>
          </button>
          <button onClick={() => navigate('/register')} className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition">
            Sign Up Free
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">
            {result ? 'Your model is ready — supports are already built in' : 'See how much filament you can save'}
          </h1>
          <p className="text-text-secondary text-base">
            {result ? 'One STL with optimized supports merged in. Print, reprint, share — no re-slicing supports ever again.' : 'Upload your STL file — get instant analysis. No signup required.'}
          </p>
        </div>

        {/* Upload Area — only when no result */}
        {!result && !loading && (
          <div className="mb-8">
            {/* Onboarding 3-step strip — sets the mental model before they
                even see the upload box. Always visible on first visit. */}
            <div
              className="flex items-center justify-center gap-2 sm:gap-3 mb-4 px-4 py-3 rounded-xl bg-gradient-to-r from-primary/10 via-bg-light/60 to-accent/10 border border-primary/15"
              data-testid="preview-onboarding-strip"
            >
              <span className="flex items-center gap-1.5 text-xs sm:text-sm text-text-secondary">
                <span className="inline-flex w-5 h-5 rounded-full bg-primary/20 items-center justify-center text-[10px] font-bold text-primary">1</span>
                <span className="text-text-primary font-semibold">Upload STL</span>
              </span>
              <ArrowRight className="w-3 h-3 text-text-muted/60 flex-shrink-0" />
              <span className="flex items-center gap-1.5 text-xs sm:text-sm text-text-secondary">
                <span className="inline-flex w-5 h-5 rounded-full bg-primary/20 items-center justify-center text-[10px] font-bold text-primary">2</span>
                <span className="text-text-primary font-semibold">Generate supports</span>
              </span>
              <ArrowRight className="w-3 h-3 text-text-muted/60 flex-shrink-0" />
              <span className="flex items-center gap-1.5 text-xs sm:text-sm text-text-secondary">
                <span className="inline-flex w-5 h-5 rounded-full bg-primary/20 items-center justify-center text-[10px] font-bold text-primary">3</span>
                <span className="text-text-primary font-semibold">Download in seconds</span>
              </span>
            </div>

            {/* Set expectation early — "Preview is free, Download needs an account" */}
            <div className="flex items-center justify-center gap-2 mb-4 px-4 py-2.5 bg-bg-light/40 border border-primary/15 rounded-full text-xs sm:text-sm text-text-secondary mx-auto w-fit" data-testid="preview-expectation-hint">
              <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span>
                <span className="text-text-primary font-semibold">Preview is free</span>
                <span className="mx-1.5 text-text-muted/50">—</span>
                <span>Download requires a <span className="text-primary font-semibold">free account</span></span>
              </span>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${
                dragOver ? 'border-primary bg-primary/5' : file ? 'border-green-500/30 bg-green-500/[0.03]' : 'border-text-muted/20 hover:border-primary/30 bg-bg-light/30'
              }`}
              onClick={() => document.getElementById('preview-file-input')?.click()}
              data-testid="preview-upload-area"
            >
              <input id="preview-file-input" type="file" accept=".stl" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
              {file ? (
                <div className="space-y-3">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto" />
                  <p className="text-text-primary font-semibold">{file.name}</p>
                  <p className="text-xs text-text-muted">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-10 h-10 text-text-muted/50 mx-auto" />
                  <p className="text-text-secondary font-medium">Drag & drop your STL file here</p>
                  <p className="text-xs text-text-muted">or click to browse — Max 25MB</p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2" data-testid="preview-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            {file && (
              <button
                onClick={handleGenerate}
                className="mt-5 w-full py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-primary/25 hover:scale-[1.01] transition-all"
                data-testid="preview-generate-btn"
              >
                <Zap className="w-5 h-5" />
                Generate Smart Supports
              </button>
            )}
          </div>
        )}

        {/* Loading — step ticker so users see continuous progress */}
        {loading && (
          <div className="bg-bg-light rounded-2xl border border-primary/20 p-8 mb-8" data-testid="preview-loading">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <Loader2 className="w-7 h-7 text-primary animate-spin" />
                <h2 className="text-lg sm:text-xl font-bold text-text-primary">Generating optimized supports...</h2>
              </div>
              <span className="text-xs font-mono text-text-muted" data-testid="preview-loading-elapsed">{loadingElapsed}s</span>
            </div>
            {[
              { label: 'Analyzing geometry', desc: 'Reading mesh & detecting overhangs' },
              { label: 'Placing support points', desc: 'AI computes optimal anchor positions' },
              { label: 'Building tree structure', desc: 'Generating lattice + trunks' },
              { label: 'Finalizing your STL', desc: 'Merging supports into ready-to-print file' },
            ].map((s, i) => {
              const done = i < loadingStep;
              const active = i === loadingStep;
              return (
                <div key={s.label} className="flex items-start gap-3 py-2" data-testid={`preview-loading-step-${i}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    done ? 'bg-green-500/15 text-green-400'
                    : active ? 'bg-primary/20 text-primary'
                    : 'bg-bg-dark/60 text-text-muted/50'
                  }`}>
                    {done ? <CheckCircle className="w-3.5 h-3.5" />
                      : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <span className="text-[10px] font-bold">{i + 1}</span>}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${
                      done ? 'text-green-400'
                      : active ? 'text-text-primary'
                      : 'text-text-muted/60'
                    }`}>
                      {s.label}{active ? '...' : ''}
                    </p>
                    {(active || done) && <p className="text-xs text-text-muted">{s.desc}</p>}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-text-muted mt-4 text-center">
              Usually takes 10–20 seconds. Don&apos;t close this window.
            </p>
          </div>
        )}

        {/* Full Results */}
        {result && previewId && (
          <div className="space-y-6 animate-[fadeIn_0.4s_ease-out]" data-testid="preview-results">
            {/* 3D Viewer */}
            <PreviewViewer previewId={previewId} />

            <div className="flex items-center justify-center gap-2 text-xs text-text-muted">
              <RotateCcw className="w-3 h-3" /> Drag to rotate — Scroll to zoom
            </div>

            {/* Savings Stats */}
            {result.support_estimate && (
              <div className="bg-gradient-to-br from-green-500/10 via-bg-light to-primary/10 rounded-xl border border-green-500/20 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Scale className="w-5 h-5 text-green-400" />
                  <h2 className="text-lg font-bold text-text-primary">Your Savings</h2>
                  {result.generation_time && <span className="text-xs text-text-muted ml-auto">Generated in {result.generation_time}s</span>}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-bg-dark/60 rounded-lg p-3">
                    <p className="text-xl font-black text-orange-300/70">{result.support_estimate.standard_grams}g</p>
                    <p className="text-[10px] text-text-muted">Standard</p>
                  </div>
                  <div className="bg-bg-dark/60 rounded-lg p-3">
                    <p className="text-xl font-black text-green-400">{result.support_estimate.smart_grams}g</p>
                    <p className="text-[10px] text-text-muted">PrintMind</p>
                  </div>
                  <div className="bg-bg-dark/60 rounded-lg p-3">
                    <p className="text-xl font-black text-green-400">{result.support_estimate.saved_percent}%</p>
                    <p className="text-[10px] text-text-muted">Saved</p>
                  </div>
                </div>
              </div>
            )}

            {/* Manual edit hint — reassures power users without diluting CTA */}
            <div className="flex items-start sm:items-center gap-3 px-4 py-3 bg-bg-light/50 border border-primary/15 rounded-xl" data-testid="preview-manual-edit-hint">
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <p className="text-xs sm:text-sm text-text-secondary">
                <span className="text-text-primary font-semibold">Not perfect?</span> Adjust support points manually after signup — add, remove, or fine-tune support density exactly where you need it.
              </p>
            </div>

            {/* Download Gate — clearly LOCKED */}
            <div className="bg-gradient-to-br from-primary/15 via-bg-light to-accent/10 rounded-2xl border-2 border-primary/30 p-6 text-center" data-testid="preview-download-gate">
              {/* Lock badge */}
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-primary/15 border-2 border-primary/40 flex items-center justify-center">
                  <Lock className="w-7 h-7 text-primary" />
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <h2 className="text-xl font-bold text-text-primary">Your model is ready — supports are already built in</h2>
              </div>
              <p className="text-sm text-text-secondary mb-5 max-w-md mx-auto">
                One ready-to-print STL with supports baked in. Create a free account to download it and reuse it as many times as you want.
              </p>
              <button
                onClick={() => {
                  trackCTAClick('Download your ready-to-print STL', 'preview-gate');
                  trackFunnelStep(FUNNEL_STEPS.DOWNLOAD_CLICK, { gate: 'preview' });
                  trackFunnelStep(FUNNEL_STEPS.SIGNUP_PROMPT_SHOWN, { trigger: 'download_gate' });
                  setShowConvertModal(true);
                }}
                className="px-8 py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 mx-auto shadow-lg shadow-primary/25 hover:scale-[1.02] transition-all"
                data-testid="preview-unlock-btn"
              >
                <Download className="w-5 h-5" />
                Download your ready-to-print STL
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-text-muted">
                <Zap className="w-3 h-3 text-primary" />
                Takes 10 seconds — no credit card required
              </div>

              {/* 24-hour file retention reassurance */}
              <div className="mt-4 pt-4 border-t border-text-muted/10 flex items-center justify-center gap-2 text-xs text-text-muted" data-testid="preview-retention-hint">
                <Clock className="w-3.5 h-3.5 text-text-muted/70" />
                <span>Your file is saved for 24 hours — create an account to keep it.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============ CONVERSION MODAL ============ */}
      {showConvertModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
          onClick={() => setShowConvertModal(false)}
          data-testid="convert-modal-backdrop"
        >
          <div
            className="relative w-full max-w-md bg-bg-light border-2 border-primary/30 rounded-2xl shadow-2xl shadow-primary/20 p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
            data-testid="convert-modal"
          >
            {/* Close */}
            <button
              onClick={() => setShowConvertModal(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full text-text-muted hover:text-text-primary hover:bg-bg-dark/50 flex items-center justify-center transition"
              data-testid="convert-modal-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Title */}
            <div className="text-center mb-5">
              <h2 className="text-2xl font-bold text-text-primary mb-1.5" data-testid="convert-modal-title">
                Almost done — sign up free to unlock your STL
              </h2>
              <p className="text-sm text-text-secondary font-medium">
                Free account. No credit card. Instant download.
              </p>
              {/* 24h deletion warning */}
              <p className="text-xs text-amber-300 mt-2 font-medium" data-testid="convert-modal-24h-warn">
                Don&apos;t lose your result — it will be deleted in 24 hours.
              </p>
            </div>

            {/* Dynamic value: filament saved + before/after */}
            {result?.support_estimate && (
              <div className="mb-5 p-4 rounded-xl bg-gradient-to-br from-green-500/10 via-bg-dark/50 to-primary/10 border border-green-500/20" data-testid="convert-modal-savings">
                <div className="text-center mb-3">
                  <p className="text-3xl font-black text-green-400 leading-none" data-testid="convert-modal-saved-percent">
                    {result.support_estimate.saved_percent}%
                  </p>
                  <p className="text-xs text-text-secondary mt-1">filament saved on this model</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-bg-dark/60 rounded-lg p-2.5 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-orange-300/70 font-bold">Standard</p>
                    <p className="text-base font-bold text-orange-300/80">{result.support_estimate.standard_grams}g</p>
                  </div>
                  <div className="bg-bg-dark/60 rounded-lg p-2.5 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-green-400 font-bold">PrintMind</p>
                    <p className="text-base font-bold text-green-400">{result.support_estimate.smart_grams}g</p>
                  </div>
                </div>
              </div>
            )}

            {/* What you get — explicit benefit list */}
            <div className="mb-5" data-testid="convert-modal-what-you-get">
              <p className="text-xs font-bold uppercase tracking-wider text-text-muted/80 mb-2.5">
                What you get with your free account
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2.5 text-text-secondary">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-text-primary font-semibold">Download this optimized STL</span> — supports already merged in</span>
                </li>
                <li className="flex items-start gap-2.5 text-text-secondary">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-text-primary font-semibold">3 free generations / month</span> for new models</span>
                </li>
                <li className="flex items-start gap-2.5 text-text-secondary">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-text-primary font-semibold">Manual fine-tuning</span> — add, remove, or adjust support points</span>
                </li>
                <li className="flex items-start gap-2.5 text-text-secondary">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-text-primary font-semibold">Save unlimited models</span> — access from any device, anytime</span>
                </li>
              </ul>
            </div>

            {/* Buttons */}
            <div className="space-y-2.5">
              <div onClick={() => trackCTAClick('Continue with Google', 'convert-modal')}>
                <GoogleAuthButton label="Continue with Google" testId="convert-modal-google-btn" />
              </div>
              <button
                onClick={() => {
                  trackCTAClick('Sign up with Email', 'convert-modal');
                  navigate('/register');
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg font-semibold text-sm shadow-lg shadow-primary/25 transition"
                data-testid="convert-modal-email-btn"
              >
                <Mail className="w-4 h-4" />
                Sign up with Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewPage;
